<template>
  <div class="media-page page-ind-kpi min-h-screen w-full p-3 md:p-4">

    <!-- ── Toolbar ── -->
    <section class="ind-toolbar mb-4">
      <div class="ind-toolbar-left">
        <div class="ind-toolbar-field">
          <label class="field-label block mb-1">Device ID</label>
          <div class="kpi-inline-controls-search-input">
            <i class="pi pi-search etq-search-icon"></i>
            <InputText
              v-model="searchQuery"
              placeholder="Buscar por Device ID…"
              class="etq-search-input kpi-table-search kpi-table-search-with-icon"
              @keyup.enter="searchDevice"
            />
          </div>
        </div>
      </div>
      <div class="ind-toolbar-right">
        <Button
          label="Solicitar logs"
          icon="pi pi-cloud-upload"
          class="p-button-sm"
          :disabled="(!currentDeviceId && (!searchQuery || searchQuery.trim().length < 8)) || requesting"
          :loading="requesting"
          @click="requestLogs"
        />
      </div>
    </section>

    <div class="kpi-page-content">
      <!-- ── Loading skeleton ── -->
      <section v-if="loading" class="kpi-section mb-4">
        <div class="dlog-loading-skel">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Buscando dispositivo...</span>
        </div>
      </section>

      <!-- ── Info del dispositivo ── -->
      <section v-if="deviceInfo" class="kpi-section mb-4">
        <div class="dlog-device-header">
          <div class="dlog-device-main">
            <span class="dlog-device-id">{{ deviceInfo.device_id || currentDeviceId }}</span>
            <span v-if="labels.length > 0" class="dlog-labels">
              <Tag v-for="(label, i) in labels" :key="i" :value="label" severity="info" class="mr-1" />
            </span>
          </div>
          <div class="dlog-device-meta">
            <span v-if="deviceInfo.platform"><strong>Plataforma:</strong> {{ deviceInfo.platform }}</span>
            <span v-if="deviceInfo.timezone"><strong>Zona:</strong> {{ deviceInfo.timezone }}</span>
            <span v-if="deviceInfo.last_seen_at"><strong>Última vez:</strong> {{ formatDate(deviceInfo.last_seen_at) }}</span>
            <span v-if="deviceInfo.ip_address"><strong>IP:</strong> {{ deviceInfo.ip_address }}</span>
          </div>
          <div v-if="deviceInfo.user_agent" class="dlog-device-ua">
            <small>{{ deviceInfo.user_agent }}</small>
          </div>
        </div>
      </section>

      <!-- ── Estado de solicitud ── -->
      <section v-if="requestStatus" class="kpi-section mb-4">
        <Message :severity="requestStatus.severity" :closable="true" @close="requestStatus = null">
          {{ requestStatus.text }}
        </Message>
      </section>

      <!-- ── Spinner mientras esperamos logs ── -->
      <section v-if="pollingActive" class="kpi-section mb-4">
        <div class="dlog-polling">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Esperando respuesta del dispositivo... ({{ pollingSeconds }}s)</span>
          <Button label="Cancelar" icon="pi pi-times" class="p-button-text p-button-sm" @click="cancelPolling" />
        </div>
      </section>

      <!-- ── Resultados ── -->
      <section v-if="logs.length > 0" class="kpi-section mb-5">
        <TabView>
          <TabPanel header="Eventos" :headerStyle="'font-size:0.82rem'">
            <div class="dlog-timeline">
              <div v-for="log in logs" :key="log.id" class="dlog-timeline-card" :class="'dlog-ev-' + log.event" @click="toggleExpand(log.id)">
                <div class="dlog-ev-bar" :class="'dlog-ev-bar--' + log.event"></div>
                <div class="dlog-ev-body">
                  <div class="dlog-ev-row1">
                    <i :class="'dlog-ev-icon ' + getEventIcon(log.event)"></i>
                    <span class="dlog-ev-label">{{ getEventLabel(log.event) }}</span>
                    <span class="dlog-ev-timeago">{{ timeAgo(log.ts) }}</span>
                    <span class="dlog-ev-time">{{ formatTime(log.ts) }}</span>
                  </div>
                  <div class="dlog-ev-row2">
                    <span v-if="log.app_mode" class="dlog-ev-tag dlog-ev-tag--mode">{{ log.app_mode }}</span>
                    <span v-if="log.online === true" class="dlog-ev-tag dlog-ev-tag--online">En línea</span>
                    <span v-else-if="log.online === false" class="dlog-ev-tag dlog-ev-tag--offline">Offline</span>
                    <span v-if="log.session_id" class="dlog-ev-session">Sesión: {{ log.session_id.slice(0, 20) }}…</span>
                  </div>
                  <div v-if="log.data" class="dlog-ev-data">
                    <template v-if="!expandedLogs[log.id]">
                      <div class="dlog-ev-fields">
                        <span v-for="(val, key) in formatEventData(log.event, log.data)" :key="key" class="dlog-ev-field">
                          <span class="dlog-ev-field-key">{{ key }}:</span>
                          <span class="dlog-ev-field-val">{{ val }}</span>
                        </span>
                      </div>
                    </template>
                    <pre v-else class="dlog-ev-raw">{{ JSON.stringify(log.data, null, 2) }}</pre>
                  </div>
                  <div v-if="log.data" class="dlog-ev-expand-hint">{{ expandedLogs[log.id] ? '▲ menos' : '▼ ver JSON completo' }}</div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel header="vBack offline" :headerStyle="'font-size:0.82rem'">
            <div v-if="vbackExport && vbackExport.length > 0">
              <p class="dlog-section-desc">{{ vbackExport.length }} reproducciones offline almacenadas localmente.</p>
              <table class="dlog-table">
                <thead><tr>
                  <th>#</th><th>URL / Archivo</th><th>Tipo</th><th>Reproducido</th>
                </tr></thead>
                <tbody>
                  <tr v-for="(vb, i) in vbackExport" :key="i">
                    <td class="dlog-tbl-num">{{ i + 1 }}</td>
                    <td class="dlog-tbl-url" :title="vb.url || vb.file || vb.path || ''">{{ vb.url || vb.file || vb.path || '—' }}</td>
                    <td>{{ vb.type || vb.mediaType || '—' }}</td>
                    <td class="dlog-tbl-date">{{ vb.playedAt || vb.ts || vb.time || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else><p class="dlog-empty-data">No hay datos de reproducción offline.</p></div>
          </TabPanel>
          <TabPanel header="Speed Tests" :headerStyle="'font-size:0.82rem'">
            <div v-if="speedtestExport && speedtestExport.length > 0">
              <p class="dlog-section-desc">{{ speedtestExport.length }} tests de velocidad registrados.</p>
              <table class="dlog-table">
                <thead><tr>
                  <th>#</th><th>Hora</th><th>Velocidad</th><th>Muestras</th>
                </tr></thead>
                <tbody>
                  <tr v-for="(st, i) in speedtestExport" :key="i">
                    <td class="dlog-tbl-num">{{ i + 1 }}</td>
                    <td class="dlog-tbl-date">{{ st.time || st.ts || st.timestamp || '—' }}</td>
                    <td>
                      <span v-if="st.mbps != null" :class="st.mbps >= 10 ? 'dlog-speed-ok' : 'dlog-speed-bad'">
                        {{ st.mbps }} Mbps
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td>{{ st.successfulSpeeds || st.successful || '—' }} / {{ st.totalSamples || st.total || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else><p class="dlog-empty-data">No hay datos de speed test.</p></div>
          </TabPanel>
          <TabPanel header="Info dispositivo" :headerStyle="'font-size:0.82rem'">
            <div v-if="deviceInfo" class="dlog-info-grid">
              <div v-for="(val, key) in deviceInfo" :key="key" class="dlog-info-row" v-show="val != null && val !== '' && val !== 0">
                <span class="dlog-info-key">{{ formatInfoKey(key) }}</span>
                <span class="dlog-info-val">{{ formatInfoVal(key, val) }}</span>
              </div>
            </div>
            <div v-else><p class="dlog-empty-data">No hay información de dispositivo.</p></div>
          </TabPanel>
        </TabView>
      </section>

      <!-- ── Sin resultados ── -->
      <section v-else-if="searched && !pollingActive && !requestStatus" class="kpi-section mb-5">
        <div class="etq-empty">
          <i class="pi pi-search etq-empty-icon etq-empty-icon--sm"></i>
          <p class="etq-empty-title" v-if="currentDeviceId">No hay logs para este dispositivo</p>
          <p class="etq-empty-subtitle">Usa "Solicitar logs" para pedir al dispositivo que suba sus registros de diagnóstico.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axiosInstance from '../axiosInstance';

export default {
    name: 'DispositivosLogs',
    emits: ['update-loading'],
    data() {
        return {
            searchQuery: '',
            currentDeviceId: '',
            logs: [],
            deviceInfo: null,
            labels: [],
            vbackExport: null,
            speedtestExport: null,
            total: 0,
            loading: false,
            searched: false,
            requesting: false,
            pollingActive: false,
            pollingSeconds: 0,
            pollingTimer: null,
            requestStatus: null,
            expandedLogs: {},
        };
    },
    methods: {
        async searchDevice() {
            const q = this.searchQuery.trim();
            if (!q || q.length < 8) {
                this.$toast.add({
                    severity: 'warn', summary: 'Aviso',
                    detail: 'Ingresa un Device ID v\u00e1lido (m\u00edn. 8 caracteres).', life: 4000, group: 'topr',
                });
                return;
            }
            this.currentDeviceId = q;
            this.loading = true;
            this.searched = true;
            this.requestStatus = null;
            try {
                const res = await axiosInstance.get('/api/device_logs_list', {
                    params: {
                        token: localStorage.getItem('token'),
                        device_id: q,
                        limit: 200,
                    },
                });
                if (res.data?.success) {
                    this.logs = res.data.logs || [];
                    this.total = res.data.total || 0;
                    this.deviceInfo = res.data.device_info || null;
                    this.labels = res.data.labels || [];
                    this.vbackExport = res.data.vback_export || null;
                    this.speedtestExport = res.data.speedtest_export || null;
                }
            } catch (e) {
                console.error('Error al buscar logs:', e);
                this.$toast.add({
                    severity: 'error', summary: 'Error',
                    detail: 'Error al consultar logs del dispositivo.', life: 5000, group: 'topr',
                });
            } finally {
                this.loading = false;
            }
        },

        async requestLogs() {
            let deviceId = this.currentDeviceId;
            if (!deviceId) {
                const q = this.searchQuery.trim();
                if (q && q.length >= 8) {
                    deviceId = q;
                    this.currentDeviceId = q;
                    this.searched = true;
                } else {
                    this.$toast.add({
                        severity: 'warn', summary: 'Aviso',
                        detail: 'Primero busca un device_id v\u00e1lido (m\u00edn. 8 caracteres).', life: 4000, group: 'topr',
                    });
                    return;
                }
            }
            this.requesting = true;
            this.requestStatus = null;
            try {
                const res = await axiosInstance.post('/api/device_logs_request', {
                    token: localStorage.getItem('token'),
                    device_id: deviceId,
                });
                if (res.data?.success) {
                    this.requestStatus = {
                        severity: 'info',
                        text: 'Solicitud enviada. Esperando respuesta del dispositivo...',
                    };
                    this.startPolling();
                } else {
                    this.requestStatus = {
                        severity: 'warn',
                        text: res.data?.message || 'No se pudo enviar la solicitud.',
                    };
                }
            } catch (e) {
                console.error('Error al solicitar logs:', e);
                this.requestStatus = {
                    severity: 'error',
                    text: 'Error al solicitar logs: ' + (e?.response?.data?.message || e.message),
                };
            } finally {
                this.requesting = false;
            }
        },

        startPolling() {
            this.pollingActive = true;
            this.pollingSeconds = 0;
            this.pollingTimer = setInterval(() => {
                this.pollingSeconds++;
                if (this.pollingSeconds > 300) {
                    this.cancelPolling();
                    this.requestStatus = {
                        severity: 'warn',
                        text: 'El dispositivo no respondi\u00f3 en 5 minutos. Puede estar desconectado o no tener la versi\u00f3n actualizada.',
                    };
                    return;
                }
                if (this.pollingSeconds % 5 === 0) {
                    this._pollCheck();
                }
            }, 1000);
        },

        async _pollCheck() {
            try {
                const res = await axiosInstance.get('/api/device_logs_list', {
                    params: {
                        token: localStorage.getItem('token'),
                        device_id: this.currentDeviceId,
                        limit: 200,
                    },
                });
                if (res.data?.success && res.data.total > this.total) {
                    const prevTotal = this.total;
                    this.logs = res.data.logs || [];
                    this.total = res.data.total;
                    this.deviceInfo = res.data.device_info || null;
                    this.labels = res.data.labels || [];
                    this.vbackExport = res.data.vback_export || null;
                    this.speedtestExport = res.data.speedtest_export || null;
                    this.cancelPolling();
                    this.requestStatus = {
                        severity: 'success',
                        text: `\u00a1Logs recibidos! (${this.total - prevTotal} nuevos, ${this.total} total)`,
                    };
                }
            } catch (e) {
                // Polling error silencioso
            }
        },

        cancelPolling() {
            this.pollingActive = false;
            this.pollingSeconds = 0;
            if (this.pollingTimer) {
                clearInterval(this.pollingTimer);
                this.pollingTimer = null;
            }
        },

        toggleExpand(id) {
            this.expandedLogs = { ...this.expandedLogs, [id]: !this.expandedLogs[id] };
        },

        getEventIcon(event) {
            const icons = {
                app_mount: 'pi pi-play',
                app_start: 'pi pi-forward',
                connection_change: 'pi pi-wifi',
                playback_error: 'pi pi-exclamation-triangle',
                vback_play: 'pi pi-save',
                token_failure: 'pi pi-ban',
                network_downtime: 'pi pi-clock',
                console_warn: 'pi pi-exclamation-circle',
                console_error: 'pi pi-times-circle',
                log_uploaded: 'pi pi-cloud-upload',
                app_error_unhandled: 'pi pi-bolt',
            };
            return icons[event] || 'pi pi-circle';
        },

        getEventLabel(event) {
            const labels = {
                app_mount: 'App iniciada',
                app_start: 'Reproducci\u00f3n iniciada',
                connection_change: 'Cambio de conexi\u00f3n',
                playback_error: 'Error de reproducci\u00f3n',
                vback_play: 'Reproducci\u00f3n offline (vBack)',
                token_failure: 'Error de token',
                network_downtime: 'Tiempo sin conexi\u00f3n',
                console_warn: 'Advertencia (console.warn)',
                console_error: 'Error (console.error)',
                log_uploaded: 'Logs subidos al servidor',
                app_error_unhandled: 'Error no controlado',
            };
            return labels[event] || event;
        },

        formatDate(dt) {
            if (!dt) return '\u2014';
            const d = new Date(dt);
            return d.toLocaleDateString('es-MX', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        },

        formatTime(ts) {
            if (!ts) return '';
            const d = new Date(ts);
            return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        },

        timeAgo(ts) {
            if (!ts) return '';
            const diff = Date.now() - new Date(ts).getTime();
            const seg = Math.floor(diff / 1000);
            if (seg < 10) return 'ahora';
            if (seg < 60) return `hace ${seg}s`;
            const min = Math.floor(seg / 60);
            if (min < 60) return `hace ${min}min`;
            const hrs = Math.floor(min / 60);
            if (hrs < 24) return `hace ${hrs}h`;
            return `hace ${Math.floor(hrs / 24)}d`;
        },

        formatEventData(event, data) {
            if (!data) return {};
            const fmt = {
                app_mount: { URL: data.url, Reproductor: data.stream, Variante: data.variant, Versi\u00f3n: data.version },
                connection_change: { 'De conexión': data.from ? 'En línea' : 'Offline', 'A conexión': data.to ? 'En línea' : 'Offline' },
                network_downtime: { Duración: this.formatDuration(data.duration_ms), 'Desde': data.from_ts ? new Date(data.from_ts).toLocaleTimeString('es-MX') : '—' },
                playback_error: { Error: data.error, Token: data.token, 'Audio ID': data.audio_id, Categoría: data.categoria },
                vback_play: { Filtro: data.filter, URL: data.url, Tipo: data.type, Razón: data.reason },
                token_failure: { Status: data.status, Error: data.error, Categoría: data.categoria, Intentos: data.attempts },
                console_warn: { Mensaje: trunc(data.message, 200) },
                console_error: { Mensaje: trunc(data.message, 200) },
                log_uploaded: { Eventos: data.count, vBacks: data.vback_count, SpeedTests: data.speedtest_count },
                app_error_unhandled: { Error: data.message },
            };
            function trunc(s, max) { return s && s.length > max ? s.slice(0, max) + '…' : s || ''; }
            const fields = fmt[event] || data;
            if (typeof fields === 'object' && !Array.isArray(fields)) return fields;
            return { Dato: JSON.stringify(fields) };
        },

        formatDuration(ms) {
            if (ms == null) return '—';
            const s = Math.floor(ms / 1000);
            if (s < 60) return `${s}s`;
            const m = Math.floor(s / 60);
            if (m < 60) return `${m}min ${s % 60}s`;
            return `${Math.floor(m / 60)}h ${m % 60}min`;
        },

        formatInfoKey(key) {
            const labels = {
                canvas_hash: 'Canvas Hash', user_agent: 'User Agent', platform: 'Plataforma',
                screen_resolution: 'Resolución', device_pixel_ratio: 'Pixel Ratio', cpu_cores: 'Núcleos CPU',
                device_memory: 'Memoria (GB)', connection_type: 'Tipo conexión', timezone: 'Zona horaria',
                language: 'Idioma', ip_address: 'Dirección IP', last_seen_at: 'Última vez visto',
                device_id: 'Device ID',
            };
            return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        },

        formatInfoVal(key, val) {
            if (key === 'last_seen_at') return this.formatDate(val);
            if (key === 'canvas_hash') return String(val).slice(0, 30) + '…';
            return val == null ? '—' : String(val);
        },
    },
    mounted() {
        // La página no carga datos al montar — la búsqueda es manual
    },
    beforeUnmount() {
        this.cancelPolling();
    },
};
</script>

<style>
@import "@/assets/media-module-base.css";

.page-ind-kpi {
  --media-hero-bg: linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%);
  --media-hero-border: rgba(147, 197, 253, 0.5);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%);
  --media-hero-border-dark: rgba(59, 130, 246, 0.2);
  max-width: 960px;
  margin: 0 auto;
}

/* ── Toolbar ── */
.page-ind-kpi .ind-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.page-ind-kpi .ind-toolbar-field {
  width: 380px;
  flex-shrink: 0;
}
.page-ind-kpi .ind-toolbar-right {
  display: flex;
  align-items: flex-end;
  padding-bottom: 2px;
}

/* ── Search input ── */
.etq-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.76rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}
.etq-search-input.p-inputtext {
  width: 100%;
  height: 42px;
  border-radius: 10px;
  padding-left: 2rem;
  font-size: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.96);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.etq-search-input.p-inputtext:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.2);
}

/* ── Device header ── */
.dlog-device-header {
  padding: 12px 16px;
}
.dlog-device-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.dlog-device-id {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.82rem;
  color: #4338ca;
  font-weight: 600;
  word-break: break-all;
}
.dlog-device-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 0.78rem;
  color: #64748b;
}
.dlog-device-ua {
  margin-top: 4px;
  color: #94a3b8;
  word-break: break-all;
}

/* ── Loading skeleton ── */
.dlog-loading-skel {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  color: #64748b;
  font-size: 0.85rem;
}

/* ── Polling indicator ── */
.dlog-polling {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: #3b82f6;
  font-size: 0.85rem;
}

/* ── Event timeline cards ── */
.dlog-timeline {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dlog-timeline-card {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.dlog-timeline-card:hover {
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.dlog-ev-bar {
  width: 4px;
  flex-shrink: 0;
}
.dlog-ev-body { flex: 1; padding: 10px 12px; min-width: 0; }

.dlog-ev-row1 {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.dlog-ev-icon { font-size: 0.8rem; width: 16px; text-align: center; }
.dlog-ev-label { font-weight: 600; font-size: 0.82rem; color: var(--text-color); }
.dlog-ev-timeago { font-size: 0.7rem; color: #94a3b8; }
.dlog-ev-time { font-size: 0.68rem; color: #cbd5e1; font-family: monospace; margin-left: auto; }

.dlog-ev-row2 { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 6px; }
.dlog-ev-tag {
  font-size: 0.64rem; padding: 1px 7px; border-radius: 8px;
  font-weight: 500;
}
.dlog-ev-tag--online { background: #dcfce7; color: #166534; }
.dlog-ev-tag--offline { background: #fee2e2; color: #991b1b; }
.dlog-ev-tag--mode { background: #e0e7ff; color: #3730a3; }
.dlog-ev-session { font-size: 0.65rem; color: #94a3b8; }

.dlog-ev-data { margin-bottom: 2px; }
.dlog-ev-fields { display: flex; flex-wrap: wrap; gap: 4px 14px; }
.dlog-ev-field { font-size: 0.74rem; color: #475569; }
.dlog-ev-field-key { color: #94a3b8; margin-right: 2px; }
.dlog-ev-field-val { color: #334155; word-break: break-all; }

.dlog-ev-raw {
  margin: 4px 0 0; font-size: 0.68rem; font-family: monospace;
  white-space: pre-wrap; word-break: break-all; color: #64748b;
  background: rgba(148, 163, 184, 0.05); padding: 8px; border-radius: 6px; max-height: 200px; overflow-y: auto;
}
.dlog-ev-expand-hint { font-size: 0.64rem; color: #94a3b8; cursor: pointer; }
.dlog-ev-expand-hint:hover { color: #64748b; }

/* ── Bar colors por evento ── */
.dlog-ev-bar { background: #94a3b8; }
.dlog-ev-bar--app_mount { background: #10b981; }
.dlog-ev-bar--app_start { background: #10b981; }
.dlog-ev-bar--connection_change { background: #3b82f6; }
.dlog-ev-bar--playback_error { background: #ef4444; }
.dlog-ev-bar--token_failure { background: #f59e0b; }
.dlog-ev-bar--network_downtime { background: #f59e0b; }
.dlog-ev-bar--vback_play { background: #8b5cf6; }
.dlog-ev-bar--console_warn { background: #f59e0b; }
.dlog-ev-bar--console_error { background: #ef4444; }
.dlog-ev-bar--log_uploaded { background: #06b6d4; }
.dlog-ev-bar--app_error_unhandled { background: #ef4444; }

/* Card bg alternates */
.dlog-timeline-card { background: rgba(148, 163, 184, 0.03); }
.dlog-timeline-card:nth-child(even) { background: rgba(148, 163, 184, 0.06); }

/* ── Tables (vBack, SpeedTests) ── */
.dlog-table {
  width: 100%; border-collapse: collapse; font-size: 0.78rem;
}
.dlog-table th {
  text-align: left; padding: 8px 10px; font-weight: 600;
  color: #64748b; border-bottom: 1px solid rgba(148,163,184,0.2);
  font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em;
}
.dlog-table td {
  padding: 8px 10px; border-bottom: 1px solid rgba(148,163,184,0.08);
  color: #334155; vertical-align: top;
}
.dlog-table tr:hover td { background: rgba(148,163,184,0.04); }
.dlog-tbl-num { color: #94a3b8; width: 30px; text-align: right; }
.dlog-tbl-url { max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: monospace; font-size: 0.72rem; }
.dlog-tbl-date { font-family: monospace; font-size: 0.72rem; color: #64748b; white-space: nowrap; }
.dlog-speed-ok { color: #166534; font-weight: 600; }
.dlog-speed-bad { color: #991b1b; font-weight: 600; }

.dlog-section-desc {
  font-size: 0.82rem; color: #64748b; margin-bottom: 10px;
}
.dlog-empty-data {
  font-size: 0.82rem; color: #94a3b8; padding: 20px;
}

/* ── Info dispositivo grid ── */
.dlog-info-grid { display: flex; flex-direction: column; }
.dlog-info-row {
  display: flex; padding: 6px 12px; font-size: 0.8rem;
  border-bottom: 1px solid rgba(148,163,184,0.06);
}
.dlog-info-row:nth-child(even) { background: rgba(148,163,184,0.03); }
.dlog-info-key { width: 160px; flex-shrink: 0; color: #94a3b8; font-weight: 500; }
.dlog-info-val { color: #334155; word-break: break-all; }

/* ── KPI page content ── */
.kpi-page-content {
  animation: kpi-content-fade-in 0.35s ease-out;
}
@keyframes kpi-content-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (max-width: 640px) {
  .page-ind-kpi .ind-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .page-ind-kpi .ind-toolbar-field {
    width: 100%;
  }
  .dlog-ev-time { display: none; }
}
</style>
